/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useState, type ReactNode } from 'react';
import AppLoader from '@/components/feedback/AppLoader';
import { useI18n } from '@/context/I18nContext';

interface TimeSeriesValueCarouselItem {
    className?: string;
    content: ReactNode;
    key: string;
}

interface TimeSeriesValueCarouselProps {
    autoAdvanceMs?: number;
    isLoading?: boolean;
    items: TimeSeriesValueCarouselItem[];
    loadingLabel?: string;
    manualPauseMs?: number;
}

function TimeSeriesValueCarousel({
    autoAdvanceMs = 8000,
    isLoading = false,
    items,
    loadingLabel,
    manualPauseMs = 0,
}: TimeSeriesValueCarouselProps) {
    const { t } = useI18n();
    const [activeIndex, setActiveIndex] = useState(0);
    const [autoAdvanceResetToken, setAutoAdvanceResetToken] = useState(0);
    const [autoAdvancePausedUntil, setAutoAdvancePausedUntil] = useState(0);
    const itemsSignature = items.map((item) => item.key).join('|');

    useEffect(() => {
        setActiveIndex(0);
    }, [itemsSignature]);

    useEffect(() => {
        if (items.length <= 1 || autoAdvancePausedUntil > Date.now()) {
            return;
        }

        const intervalId = window.setInterval(() => {
            setActiveIndex((currentIndex) => (currentIndex + 1) % items.length);
        }, autoAdvanceMs);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [autoAdvanceMs, autoAdvancePausedUntil, autoAdvanceResetToken, items.length, itemsSignature]);

    useEffect(() => {
        if (autoAdvancePausedUntil === 0) {
            return;
        }

        const remainingPauseMs = Math.max(autoAdvancePausedUntil - Date.now(), 0);
        const timeoutId = window.setTimeout(() => {
            setAutoAdvancePausedUntil(0);
        }, remainingPauseMs);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [autoAdvancePausedUntil]);

    if (items.length === 0) {
        return null;
    }

    if (items.length === 1) {
        if (isLoading) {
            return (
                <div className="time-series-kpi-carousel">
                    <AppLoader label={loadingLabel} spinnerSize="kpi" />
                </div>
            );
        }

        return items[0].content;
    }

    return (
        <div className={`time-series-kpi-carousel ${items[activeIndex]?.className ?? ''}`}>
            <div className="time-series-kpi-carousel__slide">
                {isLoading ? (
                    <AppLoader label={loadingLabel} spinnerSize="kpi" />
                ) : (
                    <div
                        key={items[activeIndex]?.key ?? 'empty'}
                        className="time-series-kpi-carousel__slide-content"
                    >
                        {items[activeIndex]?.content ?? null}
                    </div>
                )}
            </div>
            <div className="time-series-kpi-carousel__dots" aria-label={t('KPI value selection')}>
                {items.map((item, index) => {
                    const isActive = index === activeIndex;

                    return (
                        <button
                            key={item.key}
                            type="button"
                            className={`time-series-kpi-carousel__dot${
                                isActive ? ' time-series-kpi-carousel__dot--active' : ''
                            }`}
                            aria-label={t('Show KPI value {index}', { index: index + 1 })}
                            aria-pressed={isActive}
                            onClick={() => {
                                setActiveIndex(index);
                                if (manualPauseMs > 0) {
                                    setAutoAdvancePausedUntil(Date.now() + manualPauseMs);
                                }
                                setAutoAdvanceResetToken((currentValue) => currentValue + 1);
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
}

export default TimeSeriesValueCarousel;
